import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { TIERS } from "../../data/products";
import { GOALS } from "../../data/goals";
import ProductCard from "./ProductCard";
import { Leaf, Flame, Crown, Search, Loader2, X } from "lucide-react";
import { bgWithImageStyle, sectionBgColor } from "../../utils/themeBg";
import { useTheme } from "../../context/ThemeContext";
import { getTierNames } from "../../utils/tierNames";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELITE_BG =
  "https://static.prod-images.emergentagent.com/jobs/0ea60aca-0e0a-481c-bb77-149fc760bfd5/images/35d18311c34d263ff85f58a8f1c1982358715c5660a107308bc4ad3f2b08884e.png";

const tierMeta = {
  start: { icon: Leaf, number: "01", accent: "text-[#94A3B8]", line: "bg-[#94A3B8]" },
  power: { icon: Flame, number: "02", accent: "text-[#C0C0C0]", line: "bg-[#C0C0C0]" },
  elite: { icon: Crown, number: "03", accent: "text-[#E5E4E2]", line: "bg-[#E5E4E2]" },
};

const sectionBgStyle = bgWithImageStyle;

const TierSection = ({ tier, products, onOpenProduct }) => {
  const { settings } = useTheme();
  const meta = tierMeta[tier.id];
  const Icon = meta.icon;
  const tierProducts = products.filter((p) => p.tier === tier.id);

  const isElite = tier.id === "elite";

  return (
    <section
      id={tier.id}
      data-testid={`tier-section-${tier.id}`}
      className={`lv-section lv-section-${tier.id} relative py-24 md:py-32 overflow-hidden`}
      style={sectionBgStyle(sectionBgColor(settings, `catalog_${tier.id}`))}
    >
      {isElite && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${ELITE_BG})` }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundImage: "linear-gradient(to bottom, var(--lv-bg,#060A14), color-mix(in srgb, var(--lv-bg,#060A14) 85%, transparent), var(--lv-bg,#060A14))" }}
          />
        </>
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4"
            >
              <span className={`text-[11px] tracking-[0.4em] uppercase ${meta.accent}`}>
                Linea {meta.number}
              </span>
              <span className={`h-px flex-1 max-w-[100px] ${meta.line} opacity-60`} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-[Outfit] text-white tracking-tight leading-[0.95] font-light"
              style={{ fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }}
            >
              <span className="inline-flex items-baseline gap-4">
                <Icon
                  className={`w-8 h-8 md:w-10 md:h-10 ${meta.accent} self-center`}
                  strokeWidth={1.5}
                />
                {tier.name}
              </span>
              <span className="block italic font-extralight text-[#C0C0C0] text-[0.45em] mt-3 tracking-[0.05em]">
                {tier.subtitle}
              </span>
            </motion.h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-[Manrope] text-[#94A3B8] text-base sm:text-lg leading-relaxed max-w-xl"
            >
              {tier.description}
            </motion.p>
          </div>
        </div>

        {/* Product grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          data-testid={`product-grid-${tier.id}`}
        >
          {tierProducts.map((product, idx) => (
            <ProductCard
              key={product.id}
              product={product}
              index={idx}
              onOpen={onOpenProduct}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Catalog = ({ products = [], onOpenProduct }) => {
  const { settings } = useTheme();
  const tierNames = getTierNames(settings);
  const themedTiers = TIERS.map((t) => ({ ...t, ...tierNames[t.id] }));
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = idle
  const [searching, setSearching] = useState(false);
  const [activeGoal, setActiveGoal] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/catalog/search`, { params: { q } });
        setResults(data);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const goalsPresent = new Set(products.flatMap((p) => p.goals || []));
  const availableGoals = GOALS.filter((g) => goalsPresent.has(g));
  const base = results !== null ? results : products;
  const filtered = activeGoal ? base.filter((p) => (p.goals || []).includes(activeGoal)) : base;
  const showFlatGrid = results !== null || !!activeGoal;
  const resultsLabel = query.trim() && activeGoal
    ? `${filtered.length} risultati per "${query.trim()}" · Obiettivo: ${activeGoal}`
    : query.trim()
      ? `${filtered.length} risultati per "${query.trim()}"`
      : `${filtered.length} prodotti · Obiettivo: ${activeGoal}`;

  return (
    <div data-testid="catalog" id="catalogo">
      {/* Search bar */}
      <div className="border-b border-white/5 py-8" style={sectionBgStyle("var(--lv-bg,#060A14)")}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca tra i prodotti… (es. creatina, whey, omega 3)"
              data-testid="catalog-search-input"
              className="w-full bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-sm pl-11 pr-11 py-3.5 text-white text-sm focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="catalog-search-clear"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {availableGoals.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="goal-filter-bar">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mr-1">Obiettivo:</span>
              <button
                onClick={() => setActiveGoal("")}
                data-testid="goal-chip-tutti"
                className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.08em] border transition-colors ${
                  !activeGoal ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
                }`}
              >
                Tutti
              </button>
              {availableGoals.map((g) => (
                <button
                  key={g}
                  onClick={() => setActiveGoal(activeGoal === g ? "" : g)}
                  data-testid={`goal-chip-${g.toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.08em] border transition-colors ${
                    activeGoal === g ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showFlatGrid ? (
        <section className="py-14" data-testid="catalog-search-results" style={sectionBgStyle("var(--lv-bg,#060A14)")}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <p className="mb-8 text-[11px] tracking-[0.28em] uppercase text-[#94A3B8]">
              {searching ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Ricerca…</span>
              ) : (
                resultsLabel
              )}
            </p>
            {filtered.length === 0 && !searching ? (
              <p className="text-[#94A3B8] text-sm" data-testid="catalog-search-empty">
                Nessun prodotto trovato. Prova con un altro termine o obiettivo, o chiedi a Leo.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filtered.map((product, idx) => (
                  <ProductCard key={product.id} product={product} index={idx} onOpen={onOpenProduct} />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
        themedTiers.map((tier) => (
          <TierSection
            key={tier.id}
            tier={tier}
            products={products}
            onOpenProduct={onOpenProduct}
          />
        ))
      )}
    </div>
  );
};

export default Catalog;
